import type { Person } from '../Person';
import type GenericPerson from '../taaxii.com/taaxiiGenericPerson';
import type { GenericCeCo } from '../accounts/shared';

export type Direccion = {
    alias?: string
    direccion: string
    lat: number
    lon: number
  }

export type DireccionData = {
  dir: Direccion
  fechaHora: StringISO
  fechaHoraArgentina?: StringISO
  idParada: string
  idParadaPk: number
  idPasajesAsc: number[]
  idPasajesDesc: number[]
  obs: string
  secuencia: number
}

export type Pasaje = {
  idSolicitud: number
  pasajero?: Person
  despachante?: Person
  receptor?: Person
  tipo: 'PASAJERO' | 'BULTO' | 'MENSAJERIA' | 'ASIENTO VACÍO'
  paradas: [number, number] // Indices de paradas
}

export type EventType = never
  | 'registrado'
  | 'confirmado'
  | 'arribado'
  | 'circulando'
  | 'fallo'
  | 'cancelado'
  | 'anulado'
  | 'finalizado'
  | 'calificado'

export type Evento = {
  timestamp: StringISO
  type: EventType
  parada?: number // Indice a parada
  paradaData: {
    secuencia: number,
    lat: number,
    lon: number,
  }
}

export type SeguimientoData = {
  status: string
  inicio: StringISO
  distanciaCotizada: number
  cliente: {
    alias: string
    id: number
  }
  chofer: {
    personData: Person,
    puntaje: number | null
  } | null
  vehiculo: {
      marca: string
      modelo: string
      patente: string
      tipoVehiculo: {
        alias: string
        id: number
      }
    } | null
  servicio?: string
  pasajes: { [idPasaje: string]: Pasaje }
  paradas: DireccionData[]
  history: TrackerPosition[]
  eventos: Evento[]
}

export type TrackerPosition = {
  lat: number
  lon: number
  timestamp: StringISO
}

export type CarActualPosition = {
  lat: number
  lon: number
  timestamp: StringISO
  angle?: number
}

export type Recorrido = {
  isReal: boolean
  paradas: Direccion[]
  recorrido: TrackerPosition[]
}

export type PassengerModel = {
  person?: GenericPerson | Person | null,
  emisor?: GenericPerson | Person | null,
  receptor?: GenericPerson | Person | null,
  paradas?: string[] | null,
  from: string
  to: string
  ceco: GenericCeCo | null,
  obs: string | null,
  idPasaje?: number[]
}

type TrackerSSE = {
  lat: number,
  lon: number,
  alt: number,
  fecha: StringISO
}

export type EventoSeguimientoSSE = {
  choferId: number,
  viajeIdSgv: number,
  version: number,
  current: TrackerSSE & {
    parada: number,
    estado: string,
    subEvento: number
  },
}

export function mapEventoSSEToEvento(e: EventoSeguimientoSSE): Evento {
  const { current } = e;
  return {
    timestamp: current.fecha,
    type: mapDriverEventIdToEventType(current.subEvento),
    parada: current.parada,
  } as Evento;
}

export function mapEventoSSEToTrackerPosition(e: EventoSeguimientoSSE): TrackerPosition {
  return {
    lat: e.current.lat,
    lon: e.current.lon,
    timestamp: e.current.fecha,
  } as TrackerPosition;
}

export function mapEventoSSEToCarActualPosition(e: EventoSeguimientoSSE): CarActualPosition {
  return {
    ...mapEventoSSEToTrackerPosition(e),
    angle: e.current.alt,
  } as CarActualPosition;
}

export function fromIndexParadaToLabel(index: number, paradasAmount: number) {
  if (index === 0) return 'origen';
  if (index === paradasAmount - 1) return 'destino';
  return 'parada';
}

const DRIVER_EVENTS = [
  { id: 1, code: 'CIRCULANDO', label: 'Circulando' },
  { id: 2, code: 'DETENIDO', label: 'Detenido' },
  { id: 3, code: 'ESTOY_ABAJO', label: 'Estoy abajo' },
  { id: 4, code: 'FIN_DE_RECORRIDO', label: 'Fin de recorrido' },
  { id: 5, code: 'SUSPENDER_VIAJE', label: 'Suspender Viaje' },
  { id: 6, code: 'PASAJERO_SUBIO', label: 'Ascendió Pax' },
  { id: 7, code: 'PASAJERO_DESCENDIO', label: 'Descendió Pax' },
  { id: 8, code: 'PASAJERO_NO_VIAJO', label: 'No viajó Pax' },
  { id: 9, code: 'REGISTRACION', label: 'Registración' },
  { id: 10, code: 'CONSULTAR_VIAJE', label: 'Consultar Viaje' },
  { id: 11, code: 'MODIFICACION', label: 'Modificacion' },
  { id: 12, code: 'CANCELACION', label: 'Cancelacion' },
  { id: 13, code: 'RESPUESTA', label: 'Respuesta' },
  { id: 14, code: 'CONFIRMAR_VIAJE', label: 'Confirmar Viaje' },
  { id: 15, code: 'RECHAZAR_VIAJE', label: 'Rechazar Viaje' },
  { id: 16, code: 'REANUDAR', label: 'Reanudar' },
  { id: 17, code: 'PROXIMA_PARADA', label: 'Proxima parada' },
  { id: 18, code: 'EJECUTAR_VIAJE', label: 'Ejecutar Viaje' },
  { id: 20, code: 'DETENIDO_ACCIDENTE', label: 'Detenido accidente' },
  { id: 21, code: 'DETENIDO_POLICIA', label: 'Detenido policia' },
  { id: 22, code: 'DETENIDO_DESPERFECTO', label: 'Detenido desperfecto' },
  { id: 23, code: 'DETENIDO_CONGESTION', label: 'Detenido congestion' },
  { id: 24, code: 'DETENIDO_ESPERA_PAX', label: 'Detenido espera pax' },
  { id: 25, code: 'ANULACION', label: 'Anulacion' },
  { id: 26, code: 'GUARDAR', label: 'Guardar' },
  { id: 27, code: 'COMPLETADO', label: 'Completado' },
  { id: 28, code: 'FALLO', label: 'Fallo' },
  { id: 29, code: 'FINALIZAR_VIAJE', label: 'Finalizar Viaje' },
  { id: 30, code: 'GPS_ENABLE', label: 'GPS habilitado' },
  { id: 31, code: 'GPS_DISABLE', label: 'GPS deshabilitado' },
  { id: 32, code: 'GPS_OFF_SIGNAL', label: 'Sin señal de GPS' },
  { id: 33, code: 'CANCELADO_PASAJERO', label: 'Cancelado por Pasajero' },
  { id: 35, code: 'CALIFICACION', label: 'Calificado por Chofer' },
  { id: 36, code: 'RETOMANDO_CIRCULACION', label: 'Retomando circulación' },
];

export function getDriverEventById(id: number) {
  return DRIVER_EVENTS.find((event) => event.id === id);
}

export function getDriverEventCodeById(id: number): string {
  const event = getDriverEventById(id);
  return event ? event.code : '';
}

export function getDriverEventLabelById(id: number): string {
  const event = getDriverEventById(id);
  return event ? event.label : '';
}

export function mapDriverEventIdToEventType(idSubevento: number): EventType {
  const subevent = getDriverEventById(idSubevento);
  switch (subevent?.code) {
    case 'CIRCULANDO':
      return 'circulando';
    case 'ESTOY_ABAJO':
      return 'arribado';
    case 'FALLO':
      return 'fallo';
    case 'CANCELADO_PASAJERO':
      return 'cancelado';
    case 'COMPLETADO':
      return 'finalizado';
    default:
      return 'circulando';
  }
}
